import { useState, useCallback } from 'react';
import { ConfirmationModal, DialogFooter } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useSocket } from '../../context/SocketContext';
import type { ChatParticipant } from '../../types';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  members: ChatParticipant[];
}

export function CreateGroupModal({ isOpen, onClose, projectId, projectName, members }: CreateGroupModalProps) {
  const { createGroupChat } = useSocket();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const canCreate = selectedIds.size >= 2;

  const handleToggle = useCallback((memberId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    setGroupName('');
    setError('');
    setIsCreating(false);
    onClose();
  }, [onClose]);

  const handleCreate = useCallback(async () => {
    if (!canCreate) return;
    setIsCreating(true);
    setError('');
    try {
      await createGroupChat(projectId, Array.from(selectedIds), groupName.trim() || undefined);
      handleClose();
    } catch {
      setError('Failed to create group chat. Please try again.');
      setIsCreating(false);
    }
  }, [canCreate, createGroupChat, projectId, selectedIds, groupName, handleClose]);

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Group Chat"
      description={projectName}
    >
      <div className="space-y-4">
        {/* Optional group name */}
        <Input
          label="Group Name (optional)"
          placeholder="e.g. Rhythm Section, Songwriting..."
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          disabled={isCreating}
        />

        {/* Member selection */}
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-2">
            Select Members
            {selectedIds.size > 0 && (
              <span className="text-text ml-1">({selectedIds.size} selected)</span>
            )}
          </p>
          <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-border p-2">
            {members.length === 0 && (
              <p className="text-sm text-muted py-2 text-center">No bandmates in this project</p>
            )}
            {members.map((member) => (
              <label
                key={member.id}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-surface-light cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(member.id)}
                  onChange={() => handleToggle(member.id)}
                  disabled={isCreating}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.name}
                      referrerPolicy="no-referrer"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[9px] font-semibold">
                      {member.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                  )}
                </div>
                <span className="text-sm text-text truncate">{member.name}</span>
              </label>
            ))}
          </div>
          {!canCreate && selectedIds.size > 0 && (
            <p className="mt-1.5 text-xs text-muted">Select at least 2 members</p>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="ghost" onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={!canCreate} isLoading={isCreating}>
          Create Group
        </Button>
      </DialogFooter>
    </ConfirmationModal>
  );
}
